/**
 * COB2B-762 — credit balance rule. Pure functions, no DOM, no fetch.
 *
 *   remaining_balance − open_unfulfilled_orders − cart_total_incl_GST >= 0  → allow
 *                                                                  else    → block
 *
 * COB2B-794 UAT-1: every term is the invoiced amount. Open orders are counted at their
 * full totals by the backend, and the cart at its GST-inclusive Total
 * (cartTotalIncGstCents). Both were GST-exclusive before; the client corrected the
 * basis because `remaining_balance` is measured in invoiced amounts.
 *
 * Separate from the component that applies it so it can be unit-tested
 * (tests/creditRule.test.mjs, `npm test`) — the same reason
 * js/objects/catalogue-export/buildCatalogue.js is split out. This rule is where a
 * silent wrong answer is possible: nothing throws if the arithmetic is off, a
 * customer is just wrongly blocked or wrongly let through.
 *
 * ── Units ──────────────────────────────────────────────────────────────────────
 * Shopify gives cart money in **integer cents**; the company metafield and the
 * backend's order total are **decimal dollar** values. Everything below is
 * normalised to integer cents on the way in and stays there, so no comparison ever
 * runs on a float.
 */

/** Metafield/API dollars → integer cents. Null-ish stays null: see evaluateCredit. */
export function dollarsToCents(value) {
  if (value === null || value === undefined || value === '') return null;
  const amount = typeof value === 'number' ? value : parseFloat(String(value).trim());
  if (!Number.isFinite(amount)) return null;
  return Math.round(amount * 100);
}

/**
 * Normalise the company's payment_terms metafield for comparison.
 *
 * Loose by decision (COB2B-762): case, surrounding whitespace and hyphen/underscore
 * separators are all forgiven, so "On Account ", "on-account" and "ON_ACCOUNT" all
 * resolve. `cart-free-shipping-bar.liquid` does a stricter `downcase | strip` match;
 * this is the deliberately more forgiving copy.
 */
export function normalisePaymentTerms(raw) {
  if (raw === null || raw === undefined) return '';
  return String(raw).trim().toLowerCase().replace(/[-_\s]+/g, ' ');
}

/**
 * Is this company on account, and therefore subject to the credit check?
 *
 * Only an explicit match gates. Anything unrecognised — blank, "COD", a typo — is
 * treated as NOT on account and passes through unchecked. That is the deliberate
 * direction: combined with fail-closed on a missing balance, defaulting the other
 * way would block every company whose terms were never configured.
 */
export function isOnAccount(rawPaymentTerms) {
  return normalisePaymentTerms(rawPaymentTerms) === 'on account';
}

/**
 * COB2B-787 — is the company's account_status metafield "On Hold"?
 *
 * Added alongside the credit rule rather than in a module of its own: it is the
 * higher-priority half of the same one-verdict decision creditBalanceGate.js makes,
 * and it reuses the same normalisation. Nothing above it changes — the credit maths
 * and its exports are COB2B-762's and stay as they are.
 *
 * Only an explicit "on hold" gates. Absent, blank, "Active" and "Closed" all pass
 * through, matching COB2B-609's documented rule ("off hold (any other value / blank)
 * → no banner, checkout proceeds"); "Closed" is parked by decision, not overlooked.
 */
export function isAccountOnHold(rawAccountStatus) {
  return normalisePaymentTerms(rawAccountStatus) === 'on hold';
}

/**
 * The cart total the rule compares against: GST-INCLUSIVE, in cents (COB2B-794 UAT-1).
 *
 * `remaining_balance` is measured in invoiced amounts, and the open-orders term is now
 * each order's full total, so this side is the cart's Total including GST. Shipping is
 * unknowable before checkout and is left to the checkout validation function. This
 * replaced the ex-GST subtotal (gstPortionCents/cartTotalExGstCents, which mirrored
 * normal-cart-header.liquid) that the rule compared before.
 *
 * Mirrors snippets/cart-gst-totals.liquid (COB2B-774) **exactly**, so the figure checked
 * is the Total the customer sees on the cart. Keep the two in step — tests pin the
 * worked figures. What it reproduces, and why each matters:
 *
 *   - GST on TAXABLE lines only. AU has plenty of GST-free lines; a cart-level rate
 *     would charge them phantom GST and block a customer who is within their limit.
 *   - Rounded PER LINE (half-up, as Liquid's `round`), then summed — Shopify's own
 *     rounding, verified against a live checkout in COB2B-774.
 *   - An order-level discount prorates each line by total_price / items_subtotal_price,
 *     because final_line_price excludes order-level discounts.
 *   - Rate by currency (15 NZD, 10 otherwise); added to the total when the store prices
 *     tax-exclusive (both stores today), extracted from it when inclusive.
 *
 * Takes the Ajax cart JSON shape (/cart.js, /cart/change.js) — `total_price`,
 * `items_subtotal_price`, `items[].taxable`, `items[].final_line_price` — which is also
 * the shape credit-balance-data.liquid emits for first paint.
 *
 * Assumes no company location is tax-exempt (agreed for COB2B-794 UAT-1); an exempt one
 * would be shown GST here that checkout does not charge.
 */
export function cartTotalIncGstCents(cart, currencyIsoCode, taxesIncluded) {
  const total = Number(cart?.total_price) || 0;
  const itemsSubtotal = Number(cart?.items_subtotal_price) || 0;
  const rate = currencyIsoCode === 'NZD' ? 15 : 10;
  const prorate = itemsSubtotal > 0 && total < itemsSubtotal;

  let gst = 0;
  (cart?.items || []).forEach((item) => {
    if (!item || !item.taxable) return;
    let lineBase = Number(item.final_line_price) || 0;
    // Same operation order as the Liquid filters, so the floats round identically.
    if (prorate) lineBase = (lineBase * total) / itemsSubtotal;
    gst += taxesIncluded
      ? Math.round((lineBase * rate) / (rate + 100))
      : Math.round((lineBase * rate) / 100);
  });

  return taxesIncluded ? total : total + gst;
}

/**
 * The verdict.
 *
 * @param {object}  input
 * @param {number|null} input.remainingBalanceCents  company credit available, cents
 * @param {number|null} input.openOrdersTotalCents   open unfulfilled exposure, cents
 * @param {number}      input.cartTotalCents         this cart, incl. GST, cents
 * @param {boolean}     input.lookupFailed           the backend call errored/timed out
 * @returns {{blocked: boolean, reason: string, availableCents: number|null}}
 *
 * Fail-closed has two distinct triggers, both blocking:
 *   * `lookupFailed` — we asked and could not get an answer;
 *   * a null `remainingBalanceCents` — the company is on account but D3 has never
 *     populated a balance.
 *
 * Neither is the same as "this company is not on account", which is decided by
 * isOnAccount() before this function is ever called.
 */
export function evaluateCredit({
  remainingBalanceCents,
  openOrdersTotalCents,
  cartTotalCents,
  lookupFailed = false,
}) {
  if (lookupFailed) {
    return { blocked: true, reason: 'lookup-failed', availableCents: null };
  }
  if (remainingBalanceCents === null || remainingBalanceCents === undefined) {
    return { blocked: true, reason: 'no-balance', availableCents: null };
  }
  if (openOrdersTotalCents === null || openOrdersTotalCents === undefined) {
    return { blocked: true, reason: 'no-open-orders-total', availableCents: null };
  }

  const availableCents = remainingBalanceCents - openOrdersTotalCents - (Number(cartTotalCents) || 0);

  // COB2B-794 revises COB2B-762's "greater than 0" to "greater and equal to 0", so a
  // cart that lands exactly on the available balance now passes. Nothing has been
  // exceeded at zero, and the blocked copy says "exceeded".
  return {
    blocked: !(availableCents >= 0),
    reason: availableCents >= 0 ? 'ok' : 'exceeded',
    availableCents,
  };
}
